import { getExtensionValue, parseReference } from '@medplum/core';
import type { Identifier, Reference, Task } from '@medplum/fhirtypes';
import { AI_ACTION_CODE_SYSTEM, EXTENSIONS } from './constants';
import { IDENTIFIER_SYSTEMS } from './identifiers';
import { LIFESTYLE_TASK_CATEGORY_CODES, type SuggestedActionStatus, type TaskCategoryCode } from './types';

/**
 * FHIR `Task.status` → Liza suggested-action status. The single definition of the mapping: the write
 * side ({@link LIZA_STATUS_TO_FHIR}) is its inverse, and both used to be duplicated in
 * `services/suggestedAction/suggestedActionModel.ts`, where they could drift from these.
 */
export const FHIR_STATUS_TO_LIZA: Record<string, SuggestedActionStatus> = {
  requested: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  cancelled: 'expired',
};

/**
 * Liza suggested-action status → FHIR `Task.status`. `cancelled` is otherwise unused on suggestion
 * proposal Tasks, which is what makes the reverse mapping unambiguous.
 */
export const LIZA_STATUS_TO_FHIR: Record<SuggestedActionStatus, Task['status']> = {
  pending: 'requested',
  accepted: 'accepted',
  rejected: 'rejected',
  failed: 'failed',
  expired: 'cancelled',
};

/** Map a Liza suggested-action status to the FHIR `Task.status` it persists as. */
export const mapSuggestedActionStatusToFhir = (status: SuggestedActionStatus): Task['status'] =>
  LIZA_STATUS_TO_FHIR[status];

/**
 * The tool name from a suggested action Task's `code` — the coding on the `ai-action` system,
 * wherever it sits in the array. Position is not a contract: `code.coding[]` also carries the model
 * and kind codings, so reading `[0]` would return one of those once they are written.
 */
export function getSuggestedActionToolName(task: Task): string | null {
  return task.code?.coding?.find((c) => c.system === AI_ACTION_CODE_SYSTEM)?.code ?? null;
}

/** Extract the display text from a suggested action Task. */
export function getSuggestedActionDisplayText(task: Task): string {
  return task.description ?? '';
}

/** Extract the confidence score from a suggested action Task's extension. */
export function getSuggestedActionConfidence(task: Task): number {
  const value = getExtensionValue(task, EXTENSIONS.AI_SUGGESTED_ACTION_CONFIDENCE) as number | undefined;
  return value ?? 0;
}

/** Map a suggested action Task's FHIR status to Liza status. */
export function getSuggestedActionStatus(task: Task): SuggestedActionStatus {
  return FHIR_STATUS_TO_LIZA[task.status ?? ''] ?? 'pending';
}

/** Parse the params JSON from a suggested action Task's input. */
export function getSuggestedActionParams(task: Task): Record<string, unknown> {
  const input = task.input?.find((i) => i.type?.text === 'params');
  if (!input?.valueString) return {};
  try {
    return JSON.parse(input.valueString) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Extract the result message from a suggested action Task's output. */
export function getSuggestedActionResultMessage(task: Task): string | null {
  const output = task.output?.find((o) => o.type?.text === 'result-message');
  return output?.valueString ?? null;
}

/**
 * The id of the CarePlan an accepted topic suggested action created, read from the
 * `created-resource` output entry written at accept time. Null when the action created no CarePlan
 * (not yet accepted, or not a topic proposal). Lets a forward-referencing child resolve its
 * `placeholder:<id>` token to the parent topic's real CarePlan id.
 */
export function getSuggestedActionCreatedCarePlanId(task: Task): string | null {
  const output = task.output?.find((o) => o.type?.text === 'created-resource');
  const ref = output?.valueReference?.reference;
  if (!ref) return null;
  const [resourceType, id] = parseReference({ reference: ref });
  return resourceType === 'CarePlan' && id ? id : null;
}

/** Correlation key (`placeholderId`) on a topic suggested-action Task, set when a same-turn child
 * forward-references it. Null when absent. */
export function getSuggestedActionPlaceholderId(task: Task): string | null {
  return (getExtensionValue(task, EXTENSIONS.AI_SUGGESTED_ACTION_PLACEHOLDER_ID) as string | undefined) ?? null;
}

/** The `<id>` a child suggested-action Task depends on (from its `placeholder:<id>` token), naming
 * the topic suggestion (by its `placeholderId`) it attaches to. Null when the action has no dependency. */
export function getSuggestedActionDependsOnPlaceholderId(task: Task): string | null {
  return (
    (getExtensionValue(task, EXTENSIONS.AI_SUGGESTED_ACTION_DEPENDS_ON_PLACEHOLDER_ID) as string | undefined) ?? null
  );
}

/** The parent topic suggested-action Task id from a child's `partOf` (wired by the reconciliation
 * pass), or null when not linked. Lets the UI nest a child under its proposed topic. */
export function getSuggestedActionParentTaskId(task: Task): string | null {
  for (const ref of task.partOf ?? []) {
    if (!ref.reference) continue;
    const [resourceType, id] = parseReference(ref);
    if (resourceType === 'Task' && id) return id;
  }
  return null;
}

/** Extract the message ID from a suggested action Task's focus reference. */
export function getSuggestedActionMessageId(task: Task): string | null {
  if (!task.focus?.reference) return null;
  const [resourceType, id] = parseReference(task.focus);
  return resourceType === 'Communication' ? id : null;
}

/**
 * `Task.groupIdentifier` scoping a suggested-action proposal to the conversation whose run produced
 * it. Stamped at creation, so a suggestion is conversation-scoped from the moment it exists rather
 * than once the reply it belongs to has been written.
 */
export const suggestedActionConversationGroup = (conversationId: string): Identifier => ({
  system: IDENTIFIER_SYSTEMS.SUGGESTED_ACTION_CONVERSATION,
  value: conversationId,
});

/** The conversation a suggested action belongs to, or null for one created outside a thread. */
export const getSuggestedActionConversationId = (task: Task): string | null => {
  const { system, value } = task.groupIdentifier ?? {};
  return system === IDENTIFIER_SYSTEMS.SUGGESTED_ACTION_CONVERSATION ? (value ?? null) : null;
};

/**
 * When the suggestion was resolved — accepted, declined, or a failed accept — from `Task.lastModified`.
 * Null while it is still pending, and for one the TTL sweep expired: nobody resolved that, so the
 * absence is the signal.
 *
 * `lastModified` rather than `meta.lastUpdated`, which the post-acceptance `created-resource` patch
 * bumps.
 */
export const getSuggestedActionResolvedAt = (task: Task): string | null => task.lastModified ?? null;

/** Who resolved the suggestion, from `Task.owner`. Null while pending, or when it expired unresolved. */
export const getSuggestedActionResolver = (task: Task): Reference | null => task.owner ?? null;

/**
 * Topic CarePlan a suggested action attaches to, read from the Task's `basedOn`
 * (where it is persisted at creation). Null when the suggestion targets no
 * existing care plan (e.g. a new-topic proposal). Mirrors how real care-plan
 * Tasks link to their CarePlan via `basedOn`.
 */
export function getSuggestedActionCarePlanIdFromTask(task: Task): string | null {
  for (const ref of task.basedOn ?? []) {
    if (!ref.reference) continue;
    const [resourceType, id] = parseReference(ref);
    if (resourceType === 'CarePlan' && id) return id;
  }
  return null;
}

/**
 * Minimal projection of a suggested action needed to resolve which CarePlan
 * it references for preview lookup. Structurally compatible with the
 * patientpedia API's read-side suggested action shape.
 */
export interface SuggestedActionForCarePlanLookup {
  status: SuggestedActionStatus;
  toolName: string;
  params: unknown;
}

type ChildCarePlanToolName =
  | 'create_care_plan_todo'
  | 'create_care_plan_result'
  | 'create_care_plan_medication'
  | 'create_care_plan_lifestyle_change'
  | 'link_care_plan_condition'
  // Superseded wire name still carried by suggestion Tasks stored before #9820. These read
  // `Task.code.coding[].code` straight off the resource, so they never pass through the
  // registry's canonicalisation and meet both names.
  | 'create_task';

// Set<string> so .has(toolName: string) works at call sites; the satisfies clause
// catches typos and missing entries at compile time.
const CHILD_CARE_PLAN_TOOL_NAMES = new Set<string>([
  'create_care_plan_todo',
  'create_task',
  'create_care_plan_result',
  'create_care_plan_medication',
  'create_care_plan_lifestyle_change',
  'link_care_plan_condition',
] satisfies ChildCarePlanToolName[]);

/** Charset/length of a placeholder correlation key (`placeholderId`) and the `<id>` inside a
 * `placeholder:<id>` token. The single source of truth both patterns below are built from, so the
 * AI-facing schemas, the topic tool, and the parser stay in lockstep. */
const PLACEHOLDER_ID_BODY = '[a-zA-Z0-9_-]{1,64}';

/** A bare placeholder id (`placeholderId`, or the `<id>` portion of a `placeholder:<id>` token). */
export const PLACEHOLDER_ID_PATTERN = new RegExp(`^${PLACEHOLDER_ID_BODY}$`);

/** A `placeholder:<id>` forward-reference token. Capturing group 1 is the `<id>` (a {@link PLACEHOLDER_ID_PATTERN}).
 * Has no `g`/`y` flag, so it is safe to reuse across `.test`/`.exec`/Zod `.regex()` call sites. */
export const PLACEHOLDER_TOKEN_PATTERN = new RegExp(`^placeholder:(${PLACEHOLDER_ID_BODY})$`);

/**
 * Returns the CarePlan ID a pending suggested action references for preview
 * lookup, or `null` if the action is not pending or does not reference one.
 * - task and care-plan tools reference `childCarePlanId`
 * - `update_care_plan_item` and `remove_care_plan_item` reference their `carePlanId`
 */
export function getSuggestedActionCarePlanId(action: SuggestedActionForCarePlanLookup): string | null {
  if (action.status !== 'pending') return null;
  if (typeof action.params !== 'object' || action.params === null) return null;
  const params = action.params as { childCarePlanId?: unknown; carePlanId?: unknown };
  if (CHILD_CARE_PLAN_TOOL_NAMES.has(action.toolName)) {
    if (typeof params.childCarePlanId !== 'string') return null;
    // A `placeholder:<id>` token forward-references a not-yet-created topic; it is not a real
    // CarePlan id, so callers (e.g. `basedOn` writers) must not treat it as one.
    if (PLACEHOLDER_TOKEN_PATTERN.test(params.childCarePlanId)) return null;
    return params.childCarePlanId;
  }
  if (action.toolName === 'update_care_plan_item' || action.toolName === 'remove_care_plan_item') {
    return typeof params.carePlanId === 'string' ? params.carePlanId : null;
  }
  return null;
}

/**
 * The `<id>` from a child suggested action's `placeholder:<id>` `childCarePlanId`, naming
 * the same-turn topic suggestion (by its `placeholderId`) it must attach to. Null when the action is
 * not a child-attaching tool or its `childCarePlanId` is a real UUID rather than a placeholder.
 */
export function getSuggestedActionReferencedPlaceholderId(action: SuggestedActionForCarePlanLookup): string | null {
  if (typeof action.params !== 'object' || action.params === null) return null;
  if (!CHILD_CARE_PLAN_TOOL_NAMES.has(action.toolName)) return null;
  const { childCarePlanId } = action.params as { childCarePlanId?: unknown };
  if (typeof childCarePlanId !== 'string') return null;
  return PLACEHOLDER_TOKEN_PATTERN.exec(childCarePlanId)?.[1] ?? null;
}

const LIFESTYLE_TOOL_NAMES = new Set([
  'create_care_plan_lifestyle_change',
  'update_care_plan_lifestyle_change',
  'complete_care_plan_item_lifestyle_change',
]);
// Both spellings: a suggestion stored before #9820 carries the old wire name in its
// `Task.code.coding[].code`, and this set drives the card's item icon and `isCarePlanTool`'s
// review gating, neither of which goes through the registry's canonicalisation.
const TASK_TOOL_NAMES = new Set([
  'create_care_plan_todo',
  'update_care_plan_todo',
  'complete_care_plan_item_todo',
  'create_task',
  'update_care_plan_task',
  'complete_care_plan_item_task',
]);
const RESULT_TOOL_NAMES = new Set([
  'create_care_plan_result',
  'update_care_plan_result',
  'complete_care_plan_item_result',
]);
const MEDICATION_TOOL_NAMES = new Set([
  'create_care_plan_medication',
  'update_care_plan_medication',
  'complete_care_plan_item_medication',
]);
const LIFESTYLE_CATEGORY_SET = new Set<string>(LIFESTYLE_TASK_CATEGORY_CODES);

/**
 * The {@link TaskCategoryCode} a suggested-action tool targets, for the card's item icon. Lifestyle
 * changes carry the specific pillar (movement/food/sleep/mind/other) in their `category` param; tasks
 * are `clinical`; results map to `measurement` and medications to `medication`. Topic, removal, and
 * clinical-record tools have no care-plan item category and return `undefined`. `remove_care_plan_item`
 * carries no category in its name — resolve it from the referenced Task via `getTaskCategory` instead.
 */
export function getSuggestedActionTaskCategory(
  toolName: string,
  params: Record<string, unknown>,
): TaskCategoryCode | undefined {
  if (LIFESTYLE_TOOL_NAMES.has(toolName)) {
    const category = params.category;
    return typeof category === 'string' && LIFESTYLE_CATEGORY_SET.has(category)
      ? (category as TaskCategoryCode)
      : 'other';
  }
  if (TASK_TOOL_NAMES.has(toolName)) return 'clinical';
  if (RESULT_TOOL_NAMES.has(toolName)) return 'measurement';
  if (MEDICATION_TOOL_NAMES.has(toolName)) return 'medication';
  return undefined;
}
